/* The video stage and its transport (3.3).
 *
 * FRAME ACCURACY (8.6.2). Stepping is `currentTime += 1/fps`, and the fps comes
 * from ffprobe at intake — 30000/1001, not 30. Rounding that to 30 drifts by a
 * frame every ~33 seconds, which on a three-minute clip is five frames at the
 * end: enough to put a boundary on the wrong side of a hand closing.
 *
 * WHY THE PLAYHEAD IS NOT REACT STATE. rAF runs at 60fps; a setState per frame
 * would re-render the whole workspace 60 times a second. Instead the raw time
 * is pushed through a ref-based subscription, and only the components that draw
 * a moving thing (this clock, the timeline's line) read it. `onCross` fires when
 * the playhead enters a different sub-task, which is the only event the rest of
 * the app cares about.
 */

import { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { playStartFor, stepToBoundary } from "../lib/playback";
import type { RefObject } from "react";
import { clock } from "../lib/format";
import { sourceAfterError } from "../lib/media";
import type { Highlights, Subtask } from "../api/types";
import { HighlightOverlay } from "./HighlightOverlay";

/* EVERY VIDEO OPENS IN 360p (4 Nov 2026). HD used to be remembered in the
   browser, so one click on HD made every later video open the 7-25 Mbit/s
   master, which cannot start on the 0.5 Mbit/s measured from India to the
   bucket. HD now applies to the video it was chosen on, until the next video. */

const SPEEDS = [0.25, 0.5, 1, 2, 5];

export interface PlayerHandle {
  seek: (t: number) => void;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  nudge: (delta: number) => void;
  stepFrame: (direction: 1 | -1) => void;
  /** Jump to the next sub-task start, or the current one's start / the
   *  previous. What « and » do, exposed so Shift+arrow can do the same thing
   *  rather than a second, different jump under the same label. */
  stepSubtask: (direction: 1 | -1) => void;
  /** The instantaneous time, for callers that need it now (⌖ "set to the frame
   *  now showing") rather than on the next tick. */
  time: () => number;
  /** Whether the video is actually playing. Read by the timeline, which seeks
   *  on a click only while it is — see Workspace's onSelect. */
  isPlaying: () => boolean;
}

interface Props {
  ref?: RefObject<PlayerHandle | null>;
  src: string | null;
  /** 26 Oct 2026: the master, when `src` is the 360p playback copy. If the copy
   *  fails to load, the player switches to this by itself, at the same time. */
  fallbackSrc?: string | null;
  /** Shown until the first frame is decoded, instead of a black box. */
  poster?: string | null;
  /** "360p" when `src` is the playback copy. (4 Nov 2026: no longer shown —
   *  the HD / 360p toggle in the transport says which file is playing.) */
  variant?: string;
  fps: number;
  duration: number;
  /** SUB-TASK loop mode (3.3.4): when set, playback wraps inside this span.
   *  TASK mode passes null and plays straight through to the end. */
  loopSpan: { start: number; end: number } | null;
  /** Where PLAY should begin when the playhead is outside this span — the
   *  selected sub-task. Null leaves play as a plain resume. See the comment on
   *  `startPlayback` for why this is not the same as the loop span. */
  playFrom?: { start: number; end: number } | null;
  /** The TASK / SUB-TASK toggle now lives in the transport, beside the speed
   *  buttons — testing asked for it there and it belongs there: it decides what
   *  the play button does, which is what every other control on that row does
   *  too. It used to be a separate labelled row under the video carrying a
   *  sentence of explanation, which is a lot of screen for two states. */
  loopMode: "task" | "subtask";
  onLoopMode: (m: "task" | "subtask") => void;
  /** Every sub-task start time in the video, ascending — what « and » step
   *  between. They used to jump a fixed ±5 seconds, which in a tool whose unit
   *  of work is a sub-task crossed one, two or none depending on where you
   *  happened to be (testing, 21 Sep). Empty is safe: the buttons disable. */
  boundaries?: readonly number[];
  /** 4 Nov 2026 (manager's review): no longer shown on the picture. */
  overlay?: string;
  /** The footage's own pixel size, from the database. The corner caption is
   *  gone (manager's review); `height` now names the original in the quality
   *  toggle ("720p"). `width` is unused. Null when unknown; a video
   *  whose metadata never got probed still has to render.
   *
   *  It no longer sizes the stage. It used to: the stage took this aspect ratio
   *  so the picture was never letterboxed. That removed the bars inside the box
   *  and created worse ones outside it — see the stage's own comment below. */
  width?: number | null;
  height?: number | null;
  onTime: (t: number) => void;
  onDurationKnown?: (d: number) => void;
  /** 31 Oct 2026: object and hand boxes (HighlightOverlay). Absent or
   *  `available: false` hides the overlay and its button. */
  highlights?: Highlights | null;
  /** Every sub-task of the video, to know which one is under the playhead. */
  subtasks?: readonly Subtask[];
  /** 4 Nov 2026 (manager's review): highlights ON / OFF is ONE switch for every
   *  user and every video (GET/PUT /api/settings), no longer a browser's own
   *  choice. The caller owns the value; the button asks it to change. */
  highlightsOn?: boolean;
  onHighlightsChange?: (on: boolean) => void;
}

/** The file name, and nothing else.
 *
 * A presigned S3 URL is `https://bucket.s3.../key/name.mp4?X-Amz-Credential=...
 * &X-Amz-Signature=...`. The query string is a live credential; the path is
 * just where the object lives. Only the last path segment is shown, so the
 * message stays useful ("which video failed?") without putting a signature on
 * screen. Query and fragment are dropped before anything is read. */
function describeSource(src: string | null): string {
  if (!src) return "(no media path recorded for this video)";
  const path = src.split("?")[0].split("#")[0];
  const name = path.split("/").filter(Boolean).pop();
  return name ? decodeURIComponent(name) : "(unnamed object)";
}

export function Player({
  ref, src, fps, duration, loopSpan, playFrom, loopMode, onLoopMode,
  boundaries = [], onTime,
  onDurationKnown, fallbackSrc = null, poster = null, height = null,
  highlights = null, subtasks = [], highlightsOn = true, onHighlightsChange,
}: Props) {
  const showHl = highlightsOn;
  const hasHl = !!highlights?.available;
  const video = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  // A src that 404s paints nothing and reports nothing — the stage just stays
  // black, which reads as "the video is dark" rather than "the file is
  // missing". Say which.
  const [failed, setFailed] = useState(false);
  // The file actually loaded: the 360p copy, or the master after a failed copy.
  // HD: the viewer chose the master over the 360p copy (the toggle top right of
  // the picture). Not remembered: every video opens in 360p.
  const [hd, setHd] = useState(false);
  const [active, setActive] = useState<string | null>(src);
  const resumeAt = useRef<number | null>(null);
  const resumePlaying = useRef(false);
  useEffect(() => {
    setHd(false);
    setActive(src);
    setFailed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);
  const switchQuality = () => {
    if (!fallbackSrc) return;
    const v = video.current;
    resumeAt.current = v?.currentTime ?? 0;
    resumePlaying.current = !!v && !v.paused;
    const toHd = !hd;
    setHd(toHd);
    setActive(toHd ? fallbackSrc : src);
  };
  const onMediaError = () => {
    if (hd && fallbackSrc && active === fallbackSrc && src && src !== fallbackSrc) {
      resumeAt.current = video.current?.currentTime ?? 0;   // HD failed: back to the copy
      setHd(false);
      setActive(src);
      return;
    }
    const next = sourceAfterError(active, fallbackSrc);
    if (next) {
      resumeAt.current = video.current?.currentTime ?? 0;
      setActive(next);
      return;
    }
    setFailed(true);
  };
  const canSwitchQuality = !!fallbackSrc && !!src && src !== fallbackSrc && !failed;
  const playingHd = !!fallbackSrc && active === fallbackSrc;
  const hdLabel = height && height > 0 ? `${height}p` : "HD";

  // FULL SCREEN (4 Nov 2026, manager's review): the whole pane — picture and
  // transport — so every control stays usable. Esc leaves it too; the browser's
  // fullscreenchange event keeps the toggle honest either way.
  const pane = useRef<HTMLDivElement>(null);
  const [isFull, setIsFull] = useState(false);
  useEffect(() => {
    const sync = () => setIsFull(document.fullscreenElement === pane.current);
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);
  const setFull = (on: boolean) => {
    if (on && !document.fullscreenElement) void pane.current?.requestFullscreen?.().catch(() => {});
    if (!on && document.fullscreenElement) void document.exitFullscreen().catch(() => {});
  };
  const [now, setNow] = useState(0);
  const loop = useRef(loopSpan);
  loop.current = loopSpan;
  // Same pattern as `loop`: kept in a ref so `useImperativeHandle` does not
  // rebuild the handle every time the job reloads and hands over a new array.
  const bounds = useRef<readonly number[]>(boundaries);
  bounds.current = boundaries;

  const frame = 1 / (fps > 0 ? fps : 30);

  const from = useRef<{ start: number; end: number } | null>(playFrom ?? null);
  from.current = playFrom ?? null;

  /** Start playback, from the selected sub-task if we are not already in it.
   *
   *  ONE FUNCTION, BECAUSE THERE ARE FOUR WAYS TO PRESS PLAY: the transport
   *  button, Space, clicking the picture, and the imperative `play()` the
   *  workspace calls. Fixing only the button would leave the other three
   *  starting from the old position, which is exactly the kind of "sometimes it
   *  does the right thing" that is harder to report than a consistent bug.
   *
   *  OUTSIDE, NOT "NOT AT THE START". Resuming after a pause in the middle of a
   *  sub-task must continue from where it stopped — that is what pause means.
   *  The seek only happens when the playhead is somewhere this sub-task does not
   *  cover, which is the case the tester hit: the playhead at 0 (or past the
   *  end) while sub-task 5 was selected. */
  const startPlayback = useCallback(() => {
    const v = video.current;
    if (!v) return;
    // The RULE lives in lib/playback.ts with tests; this only applies it. Two
    // copies of "when should play seek" would drift, and the copy without tests
    // is the one that would drift silently.
    const at = playStartFor(v.currentTime, from.current);
    if (at !== null) v.currentTime = at;
    void v.play();
  }, []);

  const togglePlay = useCallback(() => {
    const v = video.current;
    if (!v) return;
    if (v.paused) startPlayback(); else v.pause();
  }, [startPlayback]);

  const seek = useCallback((t: number) => {
    const v = video.current;
    if (!v) return;
    const clamped = Math.max(0, Math.min(t, duration || v.duration || t));
    v.currentTime = clamped;
    setNow(clamped);
    onTime(clamped);
  }, [duration, onTime]);

  useImperativeHandle(ref, (): PlayerHandle => ({
    seek,
    play: startPlayback,
    pause: () => video.current?.pause(),
    toggle: () => {
      const v = video.current;
      if (!v) return;
      togglePlay();
    },
    nudge: (delta) => seek((video.current?.currentTime ?? 0) + delta),
    stepFrame: (direction) => {
      // Pause first: stepping while playing fights the decoder and lands
      // somewhere unpredictable.
      video.current?.pause();
      seek((video.current?.currentTime ?? 0) + direction * frame);
    },
    stepSubtask: (direction) => {
      // Reads the element rather than `now`: `now` is throttled while playing,
      // so a keypress mid-playback would step from a stale position.
      const at = stepToBoundary(video.current?.currentTime ?? 0,
                                bounds.current, direction);
      if (at !== null) seek(at);
    },
    time: () => video.current?.currentTime ?? 0,
    isPlaying: () => !!video.current && !video.current.paused && !video.current.ended,
  }), [seek, frame]);

  /** One rAF loop, started only while playing. A timeupdate listener would fire
   *  at 4Hz, which is visibly jerky for the playhead line.
   *
   *  THE LOOP CHECK RUNS EVERY FRAME; THE STATE UPDATES DO NOT.
   *
   *  This used to call setNow and onTime on all sixty frames, and onTime is
   *  Workspace's setPlayhead — so the whole workspace, inspector, task strip and
   *  timeline re-rendered sixty times a second, forever, while the video played.
   *  That is the lag testing hit on the ±5s controls: a seek asks the decoder
   *  for a new keyframe over the network at the exact moment React is busiest,
   *  and the picture stalls until both finish.
   *
   *  The file's own header says the playhead is not React state. It was.
   *
   *  15Hz is indistinguishable for a moving line and a clock, and it is a
   *  quarter of the work. The wrap check stays at 60Hz because it must catch
   *  the loop's end before the decoder runs past it. */
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    let lastPush = 0;
    const PUSH_INTERVAL_MS = 66;   // ~15Hz
    const tick = (ts: number) => {
      const v = video.current;
      if (v) {
        const span = loop.current;
        if (span && v.currentTime >= span.end - 0.01) {
          v.currentTime = span.start;      // 3.3.4: only SUB-TASK mode loops
          lastPush = 0;                    // report the wrap immediately
        }
        if (ts - lastPush >= PUSH_INTERVAL_MS) {
          lastPush = ts;
          setNow(v.currentTime);
          onTime(v.currentTime);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, onTime]);

  useEffect(() => {
    const v = video.current;
    if (v) v.playbackRate = speed;
  }, [speed]);

  const total = duration || video.current?.duration || 0;

  return (
    /* ONE PLAYER PANE: tabs-less chrome, the picture, and the transport, all on
       a single dark surface — v1.6's `.vpane`, and testing asked for it on
       17 Sep ("the video player elements are part of the video player area").

       Before this the picture was a black box and the transport sat BELOW it on
       the light page, built from the same grey pill buttons as the rest of the
       app. So the controls read as page furniture that happened to be near a
       video rather than as the video's own controls, and the black rectangle
       looked like a hole in the layout instead of a screen.

       Putting them on the same surface is what makes it a player. It also means
       the controls stop competing with the inspector's buttons for attention:
       dark chrome recedes, and the only bright thing in the column is the
       footage — which is the whole point of a review tool.

       The pane keeps `bg-black` for the same reason the stage did: testing asked
       for black in those words. v1.6 uses navy `#081626` here. */
    <div ref={pane} className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-well
      bg-black ring-1 ring-line">
      {/* THE RESERVED AREA IS THE STAGE, AND IT IS BLACK.
          Testing, 17 Sep: "some area is left blank, and that area is in white
          colour; we have to make that area black."

          The stage used to take the FOOTAGE's aspect ratio so there was nothing
          to letterbox. That removed the bars inside the box and created worse
          ones outside it: 4:3 footage in a 16:9 column left white gutters left
          and right, page background showing through either side of a black
          rectangle. Two different empties, and the outer pair looked like a
          layout bug.

          So the box fills the space the layout reserves, and the video sits
          inside it. `object-contain` keeps the aspect ratio exactly as before —
          the picture is never stretched — and whatever it does not cover is
          black, because the box is black. One empty, one colour, and it reads
          as a screen rather than as a gap.

          The video is still as large as it can be: contain fits it to whichever
          of width or height binds first. */}
      {/* THE STAGE. No background, no rounding, no ring of its own any more —
          it is a region OF the pane above, which owns all three. Giving it its
          own would draw a box inside a box.

          It still fills whatever height is left after the transport, and the
          video is centred in it with `max-h-full max-w-full`, so the aspect
          ratio is exact and whatever the picture does not cover is the pane's
          black. Do not re-introduce `aspectRatio` here — see the pane comment
          and HANDOVER §5a. */}
      <div className="relative flex min-h-0 w-full flex-1 items-center
        justify-center overflow-hidden">
        {active && !failed ? (
          <video
            ref={video}
            src={active}
            poster={poster ?? undefined}
            // FILLS THE STAGE (26 Oct 2026). `max-h-full max-w-full` never
            // scales a video UP, so the 360p copy showed at 480x360 in the
            // middle of a large black box. object-contain keeps the aspect.
            className="absolute inset-0 h-full w-full object-contain"
            // BUFFER AS SOON AS THE PAGE OPENS (4 Nov 2026): on a slow route
            // to S3, waiting for the first click to start downloading added
            // that whole wait to the moment the reviewer pressed play.
            preload="auto"
            // NO NATIVE OVERLAY ON HOVER. The element has never carried
            // `controls` — the buttons testing saw are Chrome's own
            // Picture-in-Picture overlay and media-overflow menu, which it
            // draws on any video above a size threshold whether you asked for
            // controls or not. They offer download, playback speed and PiP:
            // three ways to leave the review harness with the footage, on a
            // screen whose whole point is a controlled review of it.
            disablePictureInPicture
            controlsList="nodownload noplaybackrate noremoteplayback"
            onClick={togglePlay}
            onPlay={() => setPlaying(true)}
            onPause={() => { setPlaying(false); setNow(video.current?.currentTime ?? 0); }}
            onSeeked={() => setNow(video.current?.currentTime ?? 0)}
            onLoadedMetadata={() => {
              const d = video.current?.duration;
              if (d && isFinite(d)) onDurationKnown?.(d);
              if (resumeAt.current !== null && video.current) {
                // after a fallback or a quality switch: same moment, same state
                video.current.currentTime = resumeAt.current;
                resumeAt.current = null;
                if (resumePlaying.current) void video.current.play().catch(() => {});
                resumePlaying.current = false;
              }
            }}
            onError={onMediaError}
          />
        ) : (
          <div className="max-w-md px-6 text-center text-sm text-white/70">
            <p className="text-white">This video's file isn't here.</p>
            <p className="mt-1">
              The annotation is loaded and every control works — only the
              footage is missing.
            </p>
            {/* NEVER PRINT `src`. Once media moved to presigned S3 URLs, src
                became a signed URL carrying X-Amz-Credential (the access key
                id) and X-Amz-Signature. This message used to render it in
                full, so a missing video put a live credential on screen and
                into any screenshot a tester or client took. The file NAME is
                the only part that helps someone find the problem. */}
            <p className="mt-2 font-mono text-xs text-white/60">
              {describeSource(active ?? src)}
            </p>
          </div>
        )}

        {hasHl && showHl && active && !failed && (
          <HighlightOverlay video={video} highlights={highlights} subtasks={subtasks} />
        )}

        {/* 3.3.5: where you are, as a timecode to the millisecond. (4 Nov 2026,
            manager's review: the frame number is no longer shown.) */}
        <div className="pointer-events-none absolute left-3 top-3 rounded-chip
          bg-navy9/80 px-2 py-1 font-mono text-[11px] text-white">
          {clock(now)}.{String(Math.floor((now % 1) * 1000)).padStart(3, "0")}
        </div>
        {/* 4 Nov 2026 (manager's review): no sub-task caption (bottom left) and
            no file caption (bottom right) on the picture any more.

            QUALITY TOGGLE, TOP RIGHT OF THE PICTURE (manager's review, 3rd
            pass): "720p / 360p" in the corner, mirroring the clock top left.
            The first label is the ORIGINAL's real height (every video in the
            tool is 720 high today; a 1080 one would read 1080p), "HD" only when
            the height is unknown. z-10 keeps it above the highlight boxes. */}
        {canSwitchQuality && (
          <div className="absolute right-3 top-3 z-10 flex items-center gap-1 rounded-chip
            bg-navy9/80 p-1" role="group" aria-label="Video quality">
            {([[hdLabel, true], ["360p", false]] as const).map(([label, isHd]) => (
              <button
                key={isHd ? "hd" : "360p"}
                onClick={() => { if (playingHd !== isHd) switchQuality(); }}
                aria-pressed={playingHd === isHd}
                title={isHd ? "Play the original quality (needs a fast connection)"
                            : "Play the light 360p copy (smooth on slow connections)"}
                className={`rounded-chip px-1.5 py-0.5 font-mono text-[11px] transition
                  duration-150 ease-settle ${playingHd === isHd
                    ? "bg-white font-semibold text-navy9"
                    : "text-white/70 ring-1 ring-white/20 hover:text-white"}`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* TRANSPORT, ON THE PANE.
          Translucent white on the dark surface rather than our light grey pills
          — v1.6's `.tbtn`: `rgba(255,255,255,.07)` fill, `.18` border. Solid
          swatches would sit ON the pane as separate objects; translucency lets
          the surface show through, so the controls belong to it.

          `shrink-0` because this row must never be compressed away: it is how
          the video is watched. The stage above gives up height first. */}
      <div className="flex shrink-0 flex-wrap items-center gap-2
        border-t border-white/10 px-3 py-2 text-sm">
        {/* «  ‹𝗙  ▶  𝗙›  »  — Video_Annotation_Tool_v1.6's transport, glyphs
            and all. PLAY SITS IN THE MIDDLE with one frame-step either side of
            it and the 5-second jumps at the ends, so the row reads outward from
            the centre in both directions, matching the line of time it scrubs.
            My first pass fixed the order but left play at the left with both
            frame-steps to its right, which is a different row entirely. */}
        {/* PLAY OUTRANKS ITS NEIGHBOURS BY SIZE, not only by colour. In the
            prototype every transport button is 34px and play alone is 40px —
            filled, and the only filled one. Size survives what colour does not:
            a glance too fast to register hue, a colour-blind reviewer, a
            washed-out laptop panel outdoors. Colour was carrying that hierarchy
            on its own here, which is one signal, not two. */}
        <button
          onClick={() => { const at = stepToBoundary(now, boundaries, -1);
                           if (at !== null) seek(at); }}
          disabled={stepToBoundary(now, boundaries, -1) === null}
          className="rounded-control bg-white/10 ring-1 ring-white/20 text-white/90 transition
            duration-150 ease-settle hover:bg-white/20 hover:text-white px-2.5 py-1.5
            disabled:opacity-40 disabled:hover:bg-white/10"
          title="Restart this sub-task, or back one (Shift+←)">«</button>

        <button onClick={() => { video.current?.pause(); seek(now - frame); }}
          className="rounded-control bg-white/10 ring-1 ring-white/20 text-white/90 transition
            duration-150 ease-settle hover:bg-white/20 hover:text-white px-2.5 py-1.5 text-[11px]"
          title="1 frame back (,)">‹𝗙</button>

        {/* PLAY STAYS TEAL, where v1.6 makes it orange. Orange in our system
            means WHERE YOU ARE — the playhead line and the scrub fill, nothing
            else — and teal means "this does something". A button is an action,
            not a location, so orange here would break the one colour rule that
            lets the eye hunt for the playhead across several regions at once.
            The only place this transport knowingly departs from the deck. */}
        <button
          onClick={() => {
            const v = video.current;
            if (!v) return;
            togglePlay();
          }}
          className="rounded-control bg-teal px-5 py-2.5 text-base leading-none
            text-white transition duration-150 ease-settle hover:bg-teal7"
          title={playing ? "Pause (Space)" : "Play (Space)"}
        >
          {playing ? "❚❚" : "▶"}
        </button>

        <button onClick={() => { video.current?.pause(); seek(now + frame); }}
          className="rounded-control bg-white/10 ring-1 ring-white/20 text-white/90 transition
            duration-150 ease-settle hover:bg-white/20 hover:text-white px-2.5 py-1.5 text-[11px]"
          title="1 frame forward (.)">𝗙›</button>

        <button
          onClick={() => { const at = stepToBoundary(now, boundaries, 1);
                           if (at !== null) seek(at); }}
          disabled={stepToBoundary(now, boundaries, 1) === null}
          className="rounded-control bg-white/10 ring-1 ring-white/20 text-white/90 transition
            duration-150 ease-settle hover:bg-white/20 hover:text-white px-2.5 py-1.5
            disabled:opacity-40 disabled:hover:bg-white/10"
          title="Next sub-task (Shift+→)">»</button>

        <span className="ml-1 font-mono text-xs text-white/70">
          {clock(now)} / {clock(total)}
        </span>

        {/* full-video scrub, distinct from the timeline's current-task lane */}
        <input
          type="range"
          min={0}
          max={total || 1}
          step={frame}
          value={now}
          onChange={(e) => seek(Number(e.target.value))}
          className="mx-2 h-1 min-w-32 flex-1 cursor-pointer appearance-none
            rounded-full bg-white/20 accent-playhead"
          aria-label="Seek"
        />

        {/* Loop scope, then speed — both answer "how does this play?". The
            labels carry their own meaning, so the sentence that used to explain
            them is gone: TASK plays the task through, SUB-TASK repeats the one
            you are reviewing. */}
        {hasHl && (
          <button
            onClick={() => onHighlightsChange?.(!showHl)}
            title={showHl ? "Hide the boxes on hands and objects — for every video and every user"
                          : "Show the boxes on hands and objects — for every video and every user"}
            className={`rounded-chip px-1.5 py-1 text-xs transition duration-150 ease-settle ${showHl
              ? "bg-teal text-white" : "text-white/60 ring-1 ring-white/20 hover:text-white"}`}
          >
            ▣ HIGHLIGHTS
          </button>
        )}

        <div className="flex items-center gap-1">
          {(["task", "subtask"] as const).map((m) => (
            <button
              key={m}
              onClick={() => onLoopMode(m)}
              title={m === "task"
                ? "Play the whole task straight through"
                : "Loop around the selected sub-task"}
              className={`rounded-chip px-1.5 py-1 text-xs transition duration-150
                ease-settle ${loopMode === m
                  ? "bg-teal text-white"
                  : "text-white/60 ring-1 ring-white/20 hover:text-white"}`}
            >
              {m === "task" ? "TASK" : "SUB-TASK"}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          {SPEEDS.map((s) => (
            <button
              key={s}
              onClick={() => setSpeed(s)}
              className={`rounded-chip px-1.5 py-1 font-mono text-xs transition
                duration-150 ease-settle ${speed === s
                  ? "bg-white font-semibold text-navy9"
                  : "text-white/60 ring-1 ring-white/20 hover:text-white"}`}
            >
              {s}×
            </button>
          ))}
        </div>

        {/* FULL SCREEN, bottom-right of the player like YouTube (manager's
            review): an icon, not words. Corners pointing out = go full screen;
            corners pointing in = back to the normal view. Esc also leaves. */}
        <button
          onClick={() => setFull(!isFull)}
          aria-label={isFull ? "Exit full screen" : "Full screen"}
          title={isFull ? "Exit full screen (Esc)" : "Full screen"}
          className="ml-auto rounded-control bg-white/10 p-1.5 text-white/90 ring-1 ring-white/20 transition
            duration-150 ease-settle hover:bg-white/20 hover:text-white"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
            strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {isFull
              ? <path d="M9 4v5H4M15 4v5h5M20 15h-5v5M4 15h5v5" />
              : <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />}
          </svg>
        </button>
      </div>
    </div>
  );
}
